// Shared chart primitives + style tokens — ONE visual language for every chart in
// the app. Smooth monotone-cubic curves (curveMonotoneX, never overshoots so
// stacked bands don't cross) + gradient-filled bands. Import CHART from here so no
// chart re-invents its own palette / gridline / stroke / gradient look.

// The canonical chart style. Every chart (area, line, bar, scatter, heatmap…) reads
// these so the whole app matches — no cheap "Excel" one-offs.
export const CHART = {
  // categorical series palette, in fixed order (never cycled past the end → fold to `other`)
  palette: ['#4a9eff', '#5fd17a', '#f5b700', '#c084fc', '#f0806a', '#3fc9c0', '#e07ab0', '#9db1c8'],
  other: '#5a6472',
  // surface-neutral chrome (works on dark app + lighter chat previews)
  grid: 'rgba(140,140,140,0.16)',
  axisText: '#8a94a6',
  // marks
  stroke: 1.6,          // series line/stroke width
  strokeActive: 2.6,
  dot: 3,               // marker radius
  radius: 3,            // bar corner radius
  gapTop: 0.5, gapBot: 0.04,   // area gradient stops (top→bottom opacity)
  barTop: 0.92, barBot: 0.32,  // bar gradient stops
  dimOpacity: 0.18,     // non-focused series when hovering another
} as const;
export const colorAt = (i: number) => CHART.palette[i % CHART.palette.length];

/** Monotone-cubic (Fritsch–Carlson) spline path through the given points. */
export function monotonePath(pts: [number, number][]): string {
  const n = pts.length;
  if (n < 2) return n ? `M${pts[0][0].toFixed(2)},${pts[0][1].toFixed(2)}` : '';
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  const dx: number[] = [];
  const slope: number[] = [];
  for (let i = 0; i < n - 1; i++) { dx[i] = xs[i + 1] - xs[i]; slope[i] = (ys[i + 1] - ys[i]) / (dx[i] || 1); }
  const m: number[] = [slope[0]];
  for (let i = 1; i < n - 1; i++) {
    if (slope[i - 1] * slope[i] <= 0) { m[i] = 0; }
    else { const w1 = 2 * dx[i] + dx[i - 1], w2 = dx[i] + 2 * dx[i - 1]; m[i] = (w1 + w2) / (w1 / slope[i - 1] + w2 / slope[i]); }
  }
  m[n - 1] = slope[n - 2];
  let d = `M${xs[0].toFixed(2)},${ys[0].toFixed(2)}`;
  for (let i = 0; i < n - 1; i++) {
    const x1 = xs[i] + dx[i] / 3, y1 = ys[i] + (m[i] * dx[i]) / 3;
    const x2 = xs[i + 1] - dx[i] / 3, y2 = ys[i + 1] - (m[i + 1] * dx[i]) / 3;
    d += ` C${x1.toFixed(2)},${y1.toFixed(2)} ${x2.toFixed(2)},${y2.toFixed(2)} ${xs[i + 1].toFixed(2)},${ys[i + 1].toFixed(2)}`;
  }
  return d;
}

type Scale = (i: number) => number;
type ValScale = (v: number) => number;

/** Smooth top stroke path for a series (the visible line on top of a band). */
export function smoothLine(top: number[], xAt: Scale, yAt: ValScale): string {
  const n = top.length;
  if (n < 3) return top.map((v, i) => `${i === 0 ? 'M' : 'L'}${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`).join(' ');
  return monotonePath(top.map((v, i) => [xAt(i), yAt(v)]));
}

/** Smooth filled band between `top` and `bottom` value arrays (for gradient fill). */
export function smoothBand(top: number[], bottom: number[], xAt: Scale, yAt: ValScale): string {
  const n = top.length;
  const toPts = (arr: number[]) => arr.map((v, i) => [xAt(i), yAt(v)] as [number, number]);
  if (n < 3) {
    const up = top.map((v, i) => `${i === 0 ? 'M' : 'L'}${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`).join(' ');
    const down = bottom.map((_, i) => { const j = n - 1 - i; return `L${xAt(j).toFixed(1)},${yAt(bottom[j]).toFixed(1)}`; }).join(' ');
    return `${up} ${down} Z`;
  }
  const j = n - 1;
  const botTail = monotonePath(toPts(bottom).reverse()).replace(/^M[^C]*/, '');
  return `${monotonePath(toPts(top))} L${xAt(j).toFixed(2)},${yAt(bottom[j]).toFixed(2)}${botTail} Z`;
}
